import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { SupplierOrderStatus } from './useGetOrderDetail';
import { AutomationTarget } from './useGetOrderWorkflowStatus';

export interface ChatFeedItem {
    key: string;
    channel: 'internal' | 'vendor';
    orderId: string;
    roNumber: string;
    locationId: string;
    locationName: string;
    ymm: string;
    lastUpdated: string;
    messageCount: number;
    unread: boolean;
    unreadCount: number;
    lastSenderName?: string;
    supplierOrderId?: string;
    vendorName?: string;
    source?: AutomationTarget | 'ccc';
    status?: SupplierOrderStatus;
}

interface GetChatsResponse {
    chats: ChatFeedItem[];
    hasMore: boolean;
}

export type ChatChannelFilter = 'all' | 'internal' | 'vendor';

interface GetChatsParams {
    unread?: boolean;
    channel?: ChatChannelFilter;
    locationId?: string;
    search?: string;
    offset?: number;
}

const getChats = async (sessionId: string, params: GetChatsParams): Promise<GetChatsResponse> => {
    const query = new URLSearchParams();
    if (params.unread) query.set('unread', 'true');
    if (params.channel && params.channel !== 'all') query.set('channel', params.channel);
    if (params.locationId) query.set('locationId', params.locationId);
    if (params.search) query.set('search', params.search);
    if (params.offset) query.set('offset', params.offset.toString());

    const qs = query.toString();
    const { data } = await axios.get<GetChatsResponse>(
        `${API_URL}/chats${qs ? `?${qs}` : ''}`,
        { headers: { Authorization: sessionId } },
    );
    return data;
};

// Must match PAGE_SIZE in the getChats backend handler.
const CHAT_PAGE_SIZE = 50;

export const useGetChats = (params: GetChatsParams = {}, enabled: boolean = true) => {
    const sessionContext = useContext(SessionContext);

    return useInfiniteQuery({
        queryKey: ['/chats', params.unread, params.channel, params.locationId, params.search],
        queryFn: ({ pageParam }) =>
            getChats(sessionContext?.sessionId ?? '', { ...params, offset: pageParam }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) =>
            lastPage.hasMore ? allPages.length * CHAT_PAGE_SIZE : undefined,
        enabled: !!sessionContext?.sessionId && enabled,
        refetchInterval: params.search ? undefined : 10000,
    });
};
